import { FALLBACK_COLOR_DEFAULT } from "../../theme-constants.js";
import { COLORS, assertWixPalette, getColorValue, isRicosPalette } from "../palettes/index.js";
import {
  adaptForeground,
  canTransformToHex,
  toCssRgb,
  toHexFormat,
  toRgbTuple
} from "../theme-utils.js";
import { isRicosPaletteRgb } from "./identifiers.js";
const isNewRicosPalette = (palette) => !!palette && typeof palette === "object" && !Array.isArray(palette) && "format" in palette;
const mapRicosPaletteToLegacyPalette = (palette) => {
  const mapped = {
    actionColor: palette.accent1,
    bgColor: palette.bgColor,
    textColor: palette.textColor,
    textColorLow: palette.shade1,
    disabledColor: palette.shade2,
    fallbackColor: palette.shade3
  };
  return palette.format === "rgbTuple" ? { ...mapped, type: "rgb" } : mapped;
};
const colorToCss = (color, format) => {
  if (!color) {
    return void 0;
  }
  return format === "rgbTuple" ? `rgb(${color})` : color;
};
function omitUndefined(obj) {
  return Object.fromEntries(
    Object.entries(obj).map(
      ([k, v]) => v && typeof v === "object" && !Array.isArray(v) ? [k, omitUndefined(v)] : [k, v]
    ).filter(([, v]) => v !== void 0 && (typeof v !== "object" || Object.keys(v).length > 0))
  );
}
function mapColorPaletteToCustomStyles(colorPalette) {
  const css = (c) => colorToCss(c, colorPalette.format);
  return omitUndefined({
    button: { backgroundColor: css(colorPalette.accent1), color: css(colorPalette.bgColor) },
    secondaryButton: {
      backgroundColor: css(colorPalette.bgColor),
      color: css(colorPalette.accent1),
      borderColor: css(colorPalette.accent1)
    },
    link: { color: css(colorPalette.accent1) },
    audio: {
      backgroundColor: css(colorPalette.bgColor),
      titleColor: css(colorPalette.textColor),
      subtitleColor: css(colorPalette.shade3),
      borderColor: css(colorPalette.shade2),
      componentBorderColor: css(colorPalette.shade2),
      actionColor: css(colorPalette.accent1),
      actionTextColor: css(colorPalette.bgColor),
      sliderColor: css(colorPalette.shade1)
    },
    file: {
      backgroundColor: css(colorPalette.bgColor),
      color: css(colorPalette.textColor),
      secondaryColor: css(colorPalette.shade3),
      secondaryColorOpacity: colorPalette.shade3 ? 1 : void 0,
      borderColor: css(colorPalette.shade2),
      borderOpacity: colorPalette.shade2 ? 1 : void 0,
      borderColorHover: css(colorPalette.shade3),
      iconColor: css(colorPalette.accent1)
    },
    table: {
      backgroundColor: css(colorPalette.accent2),
      borderColor: css(colorPalette.shade2),
      color: css(colorPalette.textColor)
    },
    verticalEmbed: {
      backgroundColor: css(colorPalette.bgColor),
      color: css(colorPalette.textColor),
      textColor: css(colorPalette.shade3),
      iconColor: css(colorPalette.shade3),
      borderColor: css(colorPalette.shade2),
      ribbonFillColor: css(colorPalette.accent4),
      ribbonBorderColor: css(colorPalette.accent4),
      ribbonTextColor: css(colorPalette.textColor)
    },
    linkPreview: {
      backgroundColor: css(colorPalette.bgColor),
      borderColor: css(colorPalette.shade2),
      hoverBorderColor: css(colorPalette.shade3),
      titleColor: css(colorPalette.textColor),
      subtitleColor: css(colorPalette.shade3)
    },
    codeBlock: {
      backgroundColor: css(colorPalette.accent2),
      color: css(colorPalette.textColor)
    },
    toc: {
      color: css(colorPalette.accent1),
      titleColor: css(colorPalette.textColor),
      borderColor: css(colorPalette.shade2)
    },
    divider: { color: css(colorPalette.textColor) },
    quote: { color: css(colorPalette.textColor), borderColor: css(colorPalette.accent1) },
    layout: {
      backdropColor: css(colorPalette.accent2),
      backgroundColor: css(colorPalette.accent2)
    },
    smartBlock: {
      accentColor: css(colorPalette.accent2),
      backgroundColor: css(colorPalette.accent2),
      labelColor: css(colorPalette.accent1),
      borderColor: css(colorPalette.accent2),
      trackColor: css(colorPalette.shade2)
    }
  });
}
const createCssVars = (colors, config) => {
  const { contentBgColor = false, focusActionColor, settingsActionColor } = config || {};
  const isPaletteRgb = isRicosPaletteRgb(colors);
  const {
    textColor,
    bgColor: backgroundColor,
    actionColor,
    fallbackColor = isPaletteRgb ? "0, 0, 0" : FALLBACK_COLOR_DEFAULT,
    disabledColor,
    textColorLow,
    textOnActionColor
  } = colors;
  const parseColors = (obj) => Object.keys(obj).reduce((acc, key) => {
    const value = obj[key];
    if (value !== void 0) {
      const normalizedTuple = canTransformToHex(value) && isPaletteRgb ? toRgbTuple(toHexFormat(value)) : void 0;
      const tupleValue = normalizedTuple ?? value;
      acc[key] = isPaletteRgb ? toCssRgb(tupleValue) : tupleValue;
      acc[`${key}Tuple`] = isPaletteRgb ? tupleValue : toRgbTuple(tupleValue);
    }
    return acc;
  }, {});
  const parsedValues = parseColors({
    textColor,
    actionColor,
    backgroundColor,
    fallbackColor,
    disabledColor,
    textColorLow,
    settingsActionColor,
    focusActionColor,
    textOnActionColor
  });
  const bgColorContainer = !contentBgColor || backgroundColor === void 0 ? void 0 : isPaletteRgb ? toCssRgb(backgroundColor) : backgroundColor;
  const actionColorFallback = isPaletteRgb ? `rgb(${fallbackColor})` : adaptForeground(actionColor, fallbackColor);
  const actionColorFallbackTuple = isPaletteRgb ? fallbackColor : toRgbTuple(adaptForeground(actionColor, fallbackColor));
  return {
    ...parsedValues,
    ...actionColorFallback && { actionColorFallback },
    ...actionColorFallbackTuple && { actionColorFallbackTuple },
    ...bgColorContainer && { bgColorContainer }
  };
};
const extractColors = (palette) => {
  if (Array.isArray(palette)) {
    assertWixPalette(palette);
    return {
      actionColor: getColorValue(palette, COLORS.ACTION_COLOR),
      bgColor: getColorValue(palette, COLORS.BG_COLOR),
      textColor: getColorValue(palette, COLORS.TEXT_COLOR),
      disabledColor: getColorValue(palette, COLORS.DISABLED_COLOR),
      textColorLow: getColorValue(palette, COLORS.TEXT_COLOR_LOW)
    };
  } else if (palette && isNewRicosPalette(palette)) {
    return mapRicosPaletteToLegacyPalette(palette);
  } else if (palette && (isRicosPalette(palette) || isRicosPaletteRgb(palette))) {
    return palette;
  }
  console.error("Unrecognized Palette object. Please refer to Ricos Theme Documentation");
  return {};
};
function transformColorsToHex(colors, isPaletteRgb = false) {
  if (isPaletteRgb) {
    return { ...colors };
  }
  return Object.fromEntries(
    Object.entries(colors).map(([key, value]) => [
      key,
      typeof value === "string" && canTransformToHex(value) ? toHexFormat(value) : value
    ])
  );
}
function createPalette(palette, config) {
  if (!palette) {
    return { paletteVarsObject: {} };
  }
  const extractedColors = extractColors(palette);
  const isPaletteRgb = isRicosPaletteRgb(extractedColors);
  const transformedColors = transformColorsToHex(extractedColors, isPaletteRgb);
  return {
    paletteVarsObject: createCssVars(transformedColors, config),
    colors: transformedColors
  };
}
function createColorPalette(colorPalette, config) {
  const extractedColors = mapRicosPaletteToLegacyPalette(colorPalette);
  const isPaletteRgb = isRicosPaletteRgb(extractedColors);
  const transformedColors = transformColorsToHex(extractedColors, isPaletteRgb);
  const resolvedConfig = colorPalette.accent1 ? {
    ...config,
    settingsActionColor: colorPalette.accent1,
    focusActionColor: colorPalette.accent1
  } : config;
  return {
    paletteVarsObject: createCssVars(transformedColors, resolvedConfig),
    colors: transformedColors
  };
}
function getResolvedPaletteColors(palette, config) {
  const { paletteVarsObject } = createPalette(palette, config);
  return extractResolvedColors(paletteVarsObject);
}
function getResolvedColorPaletteColors(colorPalette, config) {
  const { paletteVarsObject } = createColorPalette(colorPalette, config);
  return extractResolvedColors(paletteVarsObject);
}
function extractResolvedColors(paletteVarsObject) {
  const getColor = (key) => {
    const value = paletteVarsObject[key];
    return typeof value === "string" ? value : void 0;
  };
  return {
    actionColor: getColor("actionColor"),
    bgColor: getColor("backgroundColor"),
    textColor: getColor("textColor"),
    textColorLow: getColor("textColorLow"),
    disabledColor: getColor("disabledColor"),
    fallbackColor: getColor("fallbackColor"),
    textOnActionColor: getColor("textOnActionColor"),
    settingsActionColor: getColor("settingsActionColor"),
    focusActionColor: getColor("focusActionColor")
  };
}
export {
  createColorPalette,
  createPalette,
  getResolvedColorPaletteColors,
  getResolvedPaletteColors,
  mapColorPaletteToCustomStyles
};
//# sourceMappingURL=palette.js.map
